import React, { useEffect, useMemo, useState } from 'react';
import { Phone, MessageCircle, Copy, Eye, RefreshCw, CalendarDays, CircleDot, UserRound } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faCircleCheck,
  faHandshakeSimple,
  faLayerGroup,
  faUserClock,
} from '@fortawesome/free-solid-svg-icons';
import { Lead, LeadStats, LeadStatus } from '../types';
import { leadsService, usersService } from '../services/api';
import PersianDatePicker from '../components/PersianDatePicker';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';

const statusOptions: Array<{ value: LeadStatus; label: string; className: string }> = [
  { value: 'NEW', label: 'جدید', className: 'bg-blue-100 text-blue-700' },
  { value: 'CONTACTED', label: 'تماس گرفته شد', className: 'bg-amber-100 text-amber-800' },
  { value: 'QUALIFIED', label: 'واجد شرایط', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'CLOSED', label: 'بسته شده', className: 'bg-slate-200 text-slate-700' },
];
const defaultStats: LeadStats = {
  total: 0,
  today: 0,
  byStatus: {
    NEW: 0,
    CONTACTED: 0,
    QUALIFIED: 0,
    CLOSED: 0,
  },
};

const overviewCards = [
  {
    key: 'total',
    label: 'کل لیدها',
    cardClassName: 'border-blue-100 from-blue-50 to-white',
    labelClassName: 'text-blue-700',
    valueClassName: 'text-blue-900',
    iconWrapperClassName: 'bg-blue-100 text-blue-700',
    icon: faLayerGroup,
  },
  {
    key: 'today',
    label: 'جدید امروز',
    cardClassName: 'border-indigo-100 from-indigo-50 to-white',
    labelClassName: 'text-indigo-700',
    valueClassName: 'text-indigo-900',
    iconWrapperClassName: 'bg-indigo-100 text-indigo-700',
    icon: faUserClock,
  },
  {
    key: 'contacted',
    label: 'در حال پیگیری',
    cardClassName: 'border-amber-100 from-amber-50 to-white',
    labelClassName: 'text-amber-700',
    valueClassName: 'text-amber-900',
    iconWrapperClassName: 'bg-amber-100 text-amber-700',
    icon: faHandshakeSimple,
  },
  {
    key: 'qualified',
    label: 'واجد شرایط',
    cardClassName: 'border-emerald-100 from-emerald-50 to-white',
    labelClassName: 'text-emerald-700',
    valueClassName: 'text-emerald-900',
    iconWrapperClassName: 'bg-emerald-100 text-emerald-700',
    icon: faBullseye,
  },
  {
    key: 'closed',
    label: 'بسته شده',
    cardClassName: 'border-slate-200 from-slate-50 to-white',
    labelClassName: 'text-slate-700',
    valueClassName: 'text-slate-900',
    iconWrapperClassName: 'bg-slate-200 text-slate-700',
    icon: faCircleCheck,
  },
] as const;

const sourceOptions = [
  { value: '', label: 'همه منابع' },
  { value: 'HEADER_CTA', label: 'دکمه هدر' },
  { value: 'HEADER_BAR_CTA', label: 'هدر بار' },
  { value: 'LANDING_PAGE', label: 'لندینگ پیج' },
  { value: 'BOOKLET_LANDING', label: 'لندینگ کتابچه' },
  { value: 'INSTAGRAM_DM', label: 'اینستاگرام' },
  { value: 'GOOGLE_ADS', label: 'گوگل ادز' },
  { value: 'REFERRAL', label: 'معرفی' },
] as const;

const callOutcomeOptions = [
  { value: 'NO_ANSWER', label: 'پاسخ نداد' },
  { value: 'FOLLOW_UP', label: 'نیاز به پیگیری' },
  { value: 'QUALIFIED', label: 'مشتری بالقوه' },
  { value: 'CLOSED', label: 'مختومه' },
] as const;

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toPersianDateTimeLabel = (value?: string) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('fa-IR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeFilterDateValue = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return toDateInputValue(parsed);
};

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<{
    status?: LeadStatus;
    search?: string;
    source?: string;
    hasNote?: boolean;
    responsibleSellerId?: string;
    createdFrom?: string;
    createdTo?: string;
  }>({});
  const [drafts, setDrafts] = useState<Record<string, { status: LeadStatus; note: string; responsibleSellerId: string }>>({});
  const [error, setError] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [notePreview, setNotePreview] = useState<{ leadName: string; text: string } | null>(null);
  const [copiedLeadId, setCopiedLeadId] = useState<string | null>(null);
  const [callReport, setCallReport] = useState({
    outcome: 'FOLLOW_UP',
    nextFollowUp: '',
    summary: '',
  });
  const [salesPersons, setSalesPersons] = useState<Array<{ id: string; name: string; phone?: string }>>([]);
  const selectedLeadId = selectedLead?.id;
  const [isCreateSellerOpen, setIsCreateSellerOpen] = useState(false);
  const [createSellerLoading, setCreateSellerLoading] = useState(false);
  const [createSellerError, setCreateSellerError] = useState('');
  const [sellerForm, setSellerForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });

  const activeFilterCount = [
    filters.status,
    filters.search,
    filters.source,
    typeof filters.hasNote === 'boolean' ? filters.hasNote : undefined,
    filters.responsibleSellerId,
    filters.createdFrom,
    filters.createdTo,
  ].filter((item) => item !== undefined && item !== '').length;

  const fetchLeads = useMemo(
    () => async (targetPage = page, targetFilters = filters) => {
      setLoading(true);
      setError('');
      try {
        const createdFrom = normalizeFilterDateValue(targetFilters.createdFrom);
        const createdTo = normalizeFilterDateValue(targetFilters.createdTo);
        const response = await leadsService.getAdminLeads({
          page: targetPage,
          limit: 20,
          status: targetFilters.status,
          search: targetFilters.search,
          source: targetFilters.source,
          hasNote: targetFilters.hasNote,
          responsibleSellerId: targetFilters.responsibleSellerId,
          createdFrom: createdFrom ? new Date(`${createdFrom}T00:00:00`).toISOString() : undefined,
          createdTo: createdTo ? new Date(`${createdTo}T23:59:59.999`).toISOString() : undefined,
        });
        setLeads(response.data || []);
        setTotalPages(response.meta?.totalPages || 1);
        setDrafts(
          Object.fromEntries(
            (response.data || []).map((lead) => [
              lead.id,
              { status: lead.status, note: lead.note || '', responsibleSellerId: lead.lastUpdatedById || '' },
            ]),
          ),
        );
      } catch (err: any) {
        setError(err?.response?.data?.message || 'خطا در دریافت لیدها');
      } finally {
        setLoading(false);
      }
    },
    [page, filters],
  );

  const fetchStats = useMemo(
    () => async () => {
      try {
        const response = await leadsService.getAdminLeadStats();
        setStats(response || defaultStats);
      } catch {
        setStats(defaultStats);
      }
    },
    [],
  );

  useEffect(() => {
    fetchLeads(page, filters);
  }, [fetchLeads, page, filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchSalesPersons = async () => {
    try {
      const users = await usersService.getSalesPersons();
      setSalesPersons(
        (users || []).map((user: any) => ({
          id: user.id,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'فروشنده',
          phone: user.phone || undefined,
        })),
      );
    } catch {
      setSalesPersons([]);
    }
  };

  useEffect(() => {
    fetchSalesPersons();
  }, []);

  useEffect(() => {
    if (!selectedLeadId) return;
    setCallReport({
      outcome: 'FOLLOW_UP',
      nextFollowUp: '',
      summary: '',
    });
  }, [selectedLeadId]);

  const saveLead = async (leadId: string) => {
    const draft = drafts[leadId];
    if (!draft) return;
    setSavingId(leadId);
    try {
      await leadsService.updateAdminLead(leadId, {
        status: draft.status,
        note: draft.note.trim() || undefined,
        responsibleSellerId: draft.responsibleSellerId || null,
      });
      await fetchLeads(page, filters);
      await fetchStats();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'خطا در ذخیره لید');
    } finally {
      setSavingId(null);
    }
  };

  const deleteLead = async (leadId: string) => {
    if (!window.confirm('این لید حذف شود؟')) return;
    try {
      await leadsService.deleteAdminLead(leadId);
      await fetchLeads(page, filters);
      await fetchStats();
      if (selectedLead?.id === leadId) {
        setSelectedLead(null);
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || 'خطا در حذف لید');
    }
  };

  const getStatusMeta = (status: LeadStatus) =>
    statusOptions.find((item) => item.value === status) || statusOptions[0];

  const toWaLink = (phone: string) => {
    const digits = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return `https://wa.me/${digits}`;
  };

  const copyPhone = async (lead: Lead) => {
    try {
      await navigator.clipboard.writeText(lead.phone);
      setCopiedLeadId(lead.id);
      window.setTimeout(() => setCopiedLeadId(null), 1200);
    } catch {
      alert('کپی شماره ناموفق بود');
    }
  };

  const applyCallReportToDraft = () => {
    if (!selectedLead) return;

    const outcomeLabel =
      callOutcomeOptions.find((item) => item.value === callReport.outcome)?.label || callReport.outcome;
    const reportLines = [
      `گزارش تماس - ${new Date().toLocaleString('fa-IR')}`,
      `نتیجه: ${outcomeLabel}`,
      callReport.nextFollowUp.trim() ? `پیگیری بعدی: ${toPersianDateTimeLabel(callReport.nextFollowUp.trim())}` : '',
      callReport.summary.trim() ? `خلاصه: ${callReport.summary.trim()}` : '',
    ].filter(Boolean);
    const reportBlock = reportLines.join('\n');

    setDrafts((prev) => {
      const current = prev[selectedLead.id] || {
        status: selectedLead.status,
        note: selectedLead.note || '',
        responsibleSellerId: selectedLead.lastUpdatedById || '',
      };
      const mergedNote = [current.note?.trim(), reportBlock].filter(Boolean).join('\n\n');
      const suggestedStatus =
        callReport.outcome === 'QUALIFIED'
          ? 'QUALIFIED'
          : callReport.outcome === 'CLOSED'
            ? 'CLOSED'
            : callReport.outcome === 'NO_ANSWER' || callReport.outcome === 'FOLLOW_UP'
              ? 'CONTACTED'
              : current.status;

      return {
        ...prev,
        [selectedLead.id]: {
          status: suggestedStatus as LeadStatus,
          note: mergedNote,
          responsibleSellerId: current.responsibleSellerId,
        },
      };
    });

    setCallReport((prev) => ({ ...prev, summary: '', nextFollowUp: '' }));
  };

  const applyQuickDateFilter = (daysBack: number) => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - daysBack);
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      createdFrom: toDateInputValue(from),
      createdTo: toDateInputValue(today),
    }));
  };

  const handleCreateSeller = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreateSellerError('');

    const firstName = sellerForm.firstName.trim();
    const lastName = sellerForm.lastName.trim();
    const phone = sellerForm.phone.trim();
    const digits = phone.replace(/\D/g, '');

    if (!firstName || !lastName) {
      setCreateSellerError('نام و نام خانوادگی فروشنده الزامی است.');
      return;
    }
    if (!digits || digits.length < 10) {
      setCreateSellerError('شماره موبایل معتبر وارد کنید.');
      return;
    }

    setCreateSellerLoading(true);
    try {
      await usersService.create({
        firstName,
        lastName,
        phone,
        role: 'SALES_PERSON',
        isActive: true,
      });
      await fetchSalesPersons();
      setSellerForm({ firstName: '', lastName: '', phone: '' });
      setIsCreateSellerOpen(false);
    } catch (err: any) {
      setCreateSellerError(err?.response?.data?.message || 'ثبت فروشنده جدید ناموفق بود.');
    } finally {
      setCreateSellerLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">مدیریت لیدها</h1>
            <p className="mt-1 text-sm text-gray-600">لیدهای ثبت‌شده از دکمه شروع در صفحه اصلی را از اینجا مدیریت کنید.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  const blob = await leadsService.exportAdminLeads({
                    status: filters.status,
                    search: filters.search,
                    source: filters.source,
                    createdFrom: filters.createdFrom ? new Date(`${filters.createdFrom}T00:00:00`).toISOString() : undefined,
                    createdTo: filters.createdTo ? new Date(`${filters.createdTo}T23:59:59.999`).toISOString() : undefined,
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'leads.xlsx';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  alert('خطا در دریافت فایل اکسل');
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              خروجی اکسل
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateSellerError('');
                setIsCreateSellerOpen(true);
              }}
              className="inline-flex items-center rounded-xl border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-sm font-bold text-fuchsia-700 hover:bg-fuchsia-100"
            >
              تعریف فروشنده مسئول
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {overviewCards.map((item) => {
          const value =
            item.key === 'total'
              ? stats.total
              : item.key === 'today'
                ? stats.today
                : item.key === 'contacted'
                  ? stats.byStatus.CONTACTED
                  : item.key === 'qualified'
                    ? stats.byStatus.QUALIFIED
                    : stats.byStatus.CLOSED;

          return (
            <div key={item.key} className={`rounded-2xl border bg-gradient-to-b p-4 ${item.cardClassName}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-xs font-semibold ${item.labelClassName}`}>{item.label}</p>
                  <p className={`mt-2 text-2xl font-black ${item.valueClassName}`}>{value.toLocaleString('fa-IR')}</p>
                </div>
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${item.iconWrapperClassName}`}>
                  <FontAwesomeIcon icon={item.icon} className="h-4 w-4" />
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-6">
        <div className="sm:col-span-2 xl:col-span-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => applyQuickDateFilter(0)}
            className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            لیدهای امروز
          </button>
          <button
            type="button"
            onClick={() => applyQuickDateFilter(6)}
            className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100"
          >
            ۷ روز اخیر
          </button>
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters((prev) => ({ ...prev, status: 'QUALIFIED' }));
            }}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
          >
            لیدهای واجد شرایط
          </button>
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters((prev) => ({ ...prev, status: 'CONTACTED', hasNote: true }));
            }}
            className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
          >
            نیازمند پیگیری
          </button>
          <button
            type="button"
            onClick={() => {
              setCreateSellerError('');
              setIsCreateSellerOpen(true);
            }}
            className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-100"
          >
            تعریف فروشنده جدید
          </button>
          {activeFilterCount > 0 ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
              {activeFilterCount.toLocaleString('fa-IR')} فیلتر فعال
            </span>
          ) : null}
        </div>
        <input
          className="sm:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm"
          placeholder="جستجو بر اساس نام، موبایل یا علاقه‌مندی"
          value={filters.search || ''}
          onChange={(e) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, search: e.target.value || undefined }));
          }}
        />
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={filters.status || ''}
          onChange={(e) => {
            setPage(1);
            const value = e.target.value as LeadStatus | '';
            setFilters((prev) => ({ ...prev, status: value || undefined }));
          }}
        >
          <option value="">همه وضعیت‌ها</option>
          {statusOptions.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={filters.source || ''}
          onChange={(e) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, source: e.target.value || undefined }));
          }}
        >
          {sourceOptions.map((item) => (
            <option key={item.value || 'all'} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          className="min-w-[13.5rem] appearance-none rounded-xl border border-gray-200 bg-white pe-9 ps-3 py-2 text-sm"
          value={filters.responsibleSellerId || ''}
          onChange={(e) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, responsibleSellerId: e.target.value || undefined }));
          }}
        >
          <option value="">فروشنده: همه</option>
          {salesPersons.map((seller) => (
            <option key={seller.id} value={seller.id}>
              {seller.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={typeof filters.hasNote === 'boolean' ? String(filters.hasNote) : ''}
          onChange={(e) => {
            setPage(1);
            const value = e.target.value;
            setFilters((prev) => ({
              ...prev,
              hasNote: value === 'true' ? true : value === 'false' ? false : undefined,
            }));
          }}
        >
          <option value="">یادداشت: همه</option>
          <option value="true">دارای یادداشت</option>
          <option value="false">بدون یادداشت</option>
        </select>
        <PersianDatePicker
          value={filters.createdFrom || ''}
          onChange={(value) => {
            setPage(1);
            setFilters((prev) => ({
              ...prev,
              createdFrom: normalizeFilterDateValue(value),
            }));
          }}
          placeholder="از تاریخ (شمسی)"
          className="min-w-[12rem]"
        />
        <PersianDatePicker
          value={filters.createdTo || ''}
          onChange={(value) => {
            setPage(1);
            setFilters((prev) => ({
              ...prev,
              createdTo: normalizeFilterDateValue(value),
            }));
          }}
          placeholder="تا تاریخ (شمسی)"
          className="min-w-[12rem]"
        />
        <button
          type="button"
          onClick={async () => {
            await fetchLeads(page, filters);
            await fetchStats();
          }}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          <RefreshCw className="h-4 w-4" />
          بروزرسانی
        </button>
        <button
          type="button"
          onClick={() => {
            setPage(1);
            setFilters({});
          }}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          پاک‌کردن فیلترها
        </button>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        {loading ? (
          <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 via-white to-white">
            <div className="flex items-center justify-between border-b border-indigo-100/70 px-4 py-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <BrandSpinnerRing size="sm" />
                <span className="text-sm font-bold">در حال بارگذاری لیدها</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-indigo-500 animate-bounce" />
                <span className="h-2 w-2 rounded-full bg-violet-500 animate-bounce [animation-delay:120ms]" />
                <span className="h-2 w-2 rounded-full bg-fuchsia-500 animate-bounce [animation-delay:240ms]" />
              </div>
            </div>
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 animate-pulse"
                >
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-8 rounded-lg bg-indigo-100/70" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="py-6 text-sm text-red-600">{error}</div>
        ) : leads.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">لیدی ثبت نشده است.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">نام</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">موبایل</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">کسب‌وکار</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">جزئیات</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">فروشنده مسئول</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">وضعیت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">یادداشت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">تاریخ ثبت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((lead) => {
                  const draft = drafts[lead.id] || {
                    status: lead.status,
                    note: lead.note || '',
                    responsibleSellerId: lead.lastUpdatedById || '',
                  };
                  const statusMeta = getStatusMeta(draft.status);

                  return (
                    <tr key={lead.id} className="hover:bg-gray-50/50">
                      <td className="px-3 py-3">
                        <p className="font-semibold text-gray-900">{lead.fullName}</p>
                        <p className="text-xs text-gray-400">{lead.source}</p>
                      </td>
                      <td className="px-3 py-3 text-gray-700" dir="ltr">
                        <div className="flex items-center gap-2">
                          <span>{lead.phone}</span>
                          <button
                            type="button"
                            title="کپی شماره"
                            onClick={() => copyPhone(lead)}
                            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {copiedLeadId === lead.id ? <span className="text-[10px] text-green-600">کپی شد</span> : null}
                      </td>
                      <td className="px-3 py-3">
                        {lead.isBusinessOwner === true ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">🏢 بله</span>
                        ) : lead.isBusinessOwner === false ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-bold text-purple-700">👤 خیر</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">
                        {lead.isBusinessOwner === true ? (
                          <div>
                            {lead.staffCount && <p>👥 {lead.staffCount}</p>}
                            {lead.businessField && <p>📌 {lead.businessField}</p>}
                          </div>
                        ) : lead.isBusinessOwner === false ? (
                          <p>{lead.jobType || '-'}</p>
                        ) : (
                          <span className="text-gray-400">{lead.interest || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 min-w-[13rem]">
                        <select
                          className="w-full min-w-[12rem] appearance-none rounded-lg border border-gray-200 bg-white pe-8 ps-2 py-2 text-xs"
                          value={draft.responsibleSellerId}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [lead.id]: {
                                ...draft,
                                responsibleSellerId: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">بدون مسئول</option>
                          {salesPersons.map((seller) => (
                            <option key={seller.id} value={seller.id}>
                              {seller.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          className="w-full rounded-lg border border-gray-200 px-2 py-2 text-xs"
                          value={draft.status}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [lead.id]: {
                                ...draft,
                                status: e.target.value as LeadStatus,
                              },
                            }))
                          }
                        >
                          {statusOptions.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <textarea
                          rows={2}
                          className="w-full rounded-lg border border-gray-200 px-2 py-2 text-xs"
                          placeholder="یادداشت پیگیری..."
                          value={draft.note}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [lead.id]: {
                                ...draft,
                                note: e.target.value,
                              },
                            }))
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setNotePreview({
                              leadName: lead.fullName,
                              text: (draft.note || '').trim() || 'یادداشتی برای این لید ثبت نشده است.',
                            })
                          }
                          className="mt-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          مشاهده کامل
                        </button>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">{new Date(lead.createdAt).toLocaleString('fa-IR')}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <a
                            href={`tel:${lead.phone}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                          >
                            <Phone className="h-3.5 w-3.5" />
                            تماس
                          </a>
                          <a
                            href={toWaLink(lead.phone)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-2.5 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            واتساپ
                          </a>
                          <button
                            type="button"
                            onClick={() => setSelectedLead(lead)}
                            className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            جزئیات
                          </button>
                          <button
                            type="button"
                            onClick={() => saveLead(lead.id)}
                            disabled={savingId === lead.id}
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                          >
                            {savingId === lead.id ? '...' : 'ذخیره'}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteLead(lead.id)}
                            className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-sm text-gray-600">
          <span>
            صفحه {page} از {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40"
            >
              قبلی
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40"
            >
              بعدی
            </button>
          </div>
        </div>
      </div>

      {notePreview ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]"
          onClick={() => setNotePreview(null)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">متن کامل یادداشت</h3>
              <button
                type="button"
                onClick={() => setNotePreview(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>
            <p className="mb-2 text-xs font-semibold text-slate-500">لید: {notePreview.leadName}</p>
            <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm leading-7 text-slate-700">
              {notePreview.text}
            </div>
          </div>
        </div>
      ) : null}

      {selectedLead ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]" onClick={() => setSelectedLead(null)}>
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-black text-slate-900">پروفایل لید</h3>
              <button
                type="button"
                onClick={() => setSelectedLead(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                    <UserRound className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-black text-slate-900">{selectedLead.fullName}</p>
                    <p className="text-xs text-slate-500">{selectedLead.source}</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl bg-white p-3">
                    <p className="text-slate-500">شماره تماس</p>
                    <p className="mt-1 font-bold text-slate-800" dir="ltr">{selectedLead.phone}</p>
                  </div>
                  <div className="rounded-xl bg-white p-3">
                    <p className="text-slate-500">علاقه‌مندی</p>
                    <p className="mt-1 font-bold text-slate-800">{selectedLead.interest || '-'}</p>
                  </div>
                  <div className="rounded-xl bg-white p-3">
                    <p className="text-slate-500">تاریخ ثبت</p>
                    <p className="mt-1 font-bold text-slate-800">{new Date(selectedLead.createdAt).toLocaleString('fa-IR')}</p>
                  </div>
                  <div className="rounded-xl bg-white p-3">
                    <p className="text-slate-500">آخرین تماس</p>
                    <p className="mt-1 font-bold text-slate-800">{selectedLead.contactedAt ? new Date(selectedLead.contactedAt).toLocaleString('fa-IR') : 'ثبت نشده'}</p>
                  </div>
                  <div className="rounded-xl bg-white p-3 col-span-2">
                    <p className="text-slate-500">فروشنده مسئول</p>
                    <p className="mt-1 font-bold text-slate-800">
                      {selectedLead.responsibleSeller
                        ? `${selectedLead.responsibleSeller.firstName || ''} ${selectedLead.responsibleSeller.lastName || ''}`.trim() ||
                          selectedLead.responsibleSeller.username ||
                          'ثبت نشده'
                        : 'ثبت نشده'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="mb-3 text-sm font-bold text-slate-800">اقدام سریع</p>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`tel:${selectedLead.phone}`}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    <Phone className="h-4 w-4" />
                    تماس مستقیم
                  </a>
                  <a
                    href={toWaLink(selectedLead.phone)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-3 py-2 text-xs font-bold text-white hover:bg-green-700"
                  >
                    <MessageCircle className="h-4 w-4" />
                    پیام واتساپ
                  </a>
                  <button
                    type="button"
                    onClick={() => copyPhone(selectedLead)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <Copy className="h-4 w-4" />
                    کپی شماره
                  </button>
                  <button
                    type="button"
                    onClick={() => window.open(`sms:${selectedLead.phone}`)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <MessageCircle className="h-4 w-4" />
                    ارسال پیامک
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="mb-3 text-sm font-bold text-slate-800">وضعیت و یادداشت</p>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">فروشنده مسئول</label>
                    <select
                      value={drafts[selectedLead.id]?.responsibleSellerId ?? selectedLead.lastUpdatedById ?? ''}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [selectedLead.id]: {
                            status: prev[selectedLead.id]?.status ?? selectedLead.status,
                            note: prev[selectedLead.id]?.note ?? selectedLead.note ?? '',
                            responsibleSellerId: e.target.value,
                          },
                        }))
                      }
                      className="w-full appearance-none rounded-xl border border-slate-200 bg-white pe-9 ps-3 py-2 text-sm"
                    >
                      <option value="">بدون مسئول</option>
                      {salesPersons.map((seller) => (
                        <option key={seller.id} value={seller.id}>
                          {seller.name}{seller.phone ? ` - ${seller.phone}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">وضعیت لید</label>
                    <select
                      value={drafts[selectedLead.id]?.status || selectedLead.status}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [selectedLead.id]: {
                            status: e.target.value as LeadStatus,
                            note: prev[selectedLead.id]?.note ?? selectedLead.note ?? '',
                            responsibleSellerId: prev[selectedLead.id]?.responsibleSellerId ?? selectedLead.lastUpdatedById ?? '',
                          },
                        }))
                      }
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">یادداشت ارتباط</label>
                    <textarea
                      rows={4}
                      value={drafts[selectedLead.id]?.note ?? selectedLead.note ?? ''}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [selectedLead.id]: {
                            status: prev[selectedLead.id]?.status ?? selectedLead.status,
                            note: e.target.value,
                            responsibleSellerId: prev[selectedLead.id]?.responsibleSellerId ?? selectedLead.lastUpdatedById ?? '',
                          },
                        }))
                      }
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      placeholder="خلاصه تماس، نیاز مشتری و اقدام بعدی..."
                    />
                  </div>

                  <button
                    type="button"
                    onClick={async () => {
                      await saveLead(selectedLead.id);
                      const latest = leads.find((item) => item.id === selectedLead.id);
                      if (latest) {
                        setSelectedLead(latest);
                      }
                    }}
                    disabled={savingId === selectedLead.id}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    <CircleDot className="h-4 w-4" />
                    {savingId === selectedLead.id ? 'در حال ذخیره...' : 'ذخیره تغییرات لید'}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="mb-3 text-sm font-bold text-slate-800">گزارش تماس</p>
                <div className="mb-3 rounded-xl border border-amber-100 bg-amber-50/70 p-3 text-xs text-amber-900">
                  <p className="font-bold">چه گزارشی ثبت کنیم؟</p>
                  <p className="mt-1">نتیجه تماس + نیاز اصلی مشتری + مانع خرید + اقدام بعدی (تاریخ پیگیری) را وارد کنید.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">نتیجه تماس</label>
                    <select
                      value={callReport.outcome}
                      onChange={(e) => setCallReport((prev) => ({ ...prev, outcome: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      {callOutcomeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">زمان پیگیری بعدی</label>
                    <PersianDatePicker
                      value={callReport.nextFollowUp}
                      onChange={(value) => setCallReport((prev) => ({ ...prev, nextFollowUp: value }))}
                      placeholder="زمان پیگیری را انتخاب کنید"
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">خلاصه مکالمه</label>
                  <textarea
                    rows={3}
                    value={callReport.summary}
                    onChange={(e) => setCallReport((prev) => ({ ...prev, summary: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="نیاز مشتری، اعتراض‌ها و اقدام بعدی را بنویسید..."
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCallReportToDraft}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
                >
                  افزودن گزارش به یادداشت لید
                </button>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="mb-2 text-sm font-bold text-slate-800">یادآوری پیگیری</p>
                <p className="text-xs text-slate-500">برای تجربه بهتر تیم فروش، تاریخ تماس بعدی را در note بنویسید (مثال: پیگیری در ۱۴۰۵/۰۲/۰۵ ساعت ۱۶).</p>
                <div className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600">
                  <CalendarDays className="h-4 w-4" />
                  ثبت یادآوری در یادداشت هر لید
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isCreateSellerOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]"
          onClick={() => setIsCreateSellerOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">تعریف فروشنده جدید</h3>
              <button
                type="button"
                onClick={() => setIsCreateSellerOpen(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>

            <form onSubmit={handleCreateSeller} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">نام</label>
                  <input
                    type="text"
                    value={sellerForm.firstName}
                    onChange={(e) => setSellerForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="مثال: حسین"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">نام خانوادگی</label>
                  <input
                    type="text"
                    value={sellerForm.lastName}
                    onChange={(e) => setSellerForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="مثال: گرامی"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">شماره موبایل</label>
                <input
                  type="tel"
                  dir="ltr"
                  value={sellerForm.phone}
                  onChange={(e) => setSellerForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-left"
                  placeholder="09xxxxxxxxx"
                  required
                />
              </div>

              {createSellerError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{createSellerError}</p>
              ) : null}

              <button
                type="submit"
                disabled={createSellerLoading}
                className="inline-flex w-full items-center justify-center rounded-xl bg-fuchsia-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-fuchsia-700 disabled:opacity-60"
              >
                {createSellerLoading ? 'در حال ثبت...' : 'ثبت فروشنده'}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Leads;
